import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../../lib/supabase';

const S = {
  page: { minHeight: '100vh', background: '#0f1115', padding: '24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: 800, color: '#fff' },
  nav: { display: 'flex', gap: '8px' },
  navBtn: { background: '#2a2d35', color: '#9ca3af', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' },
  navBtnActive: { background: '#00e701', color: '#000' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },
  statCard: { background: '#1a1d24', borderRadius: '12px', padding: '20px', border: '1px solid #2a2d35' },
  statVal: { fontSize: '24px', fontWeight: 800, color: '#fff', marginBottom: '4px' },
  statLabel: { fontSize: '12px', color: '#6b7280' },
  statGreen: { color: '#00e701' },
  statYellow: { color: '#fcb10f' },
  statRed: { color: '#ff4444' },
  statBlue: { color: '#3b82f6' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab: { background: '#2a2d35', color: '#9ca3af', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
  tabActive: { background: '#00e701', color: '#000' },
  table: { background: '#1a1d24', borderRadius: '12px', border: '1px solid #2a2d35', overflow: 'hidden' },
  th: { background: '#0f1115', padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 },
  td: { padding: '14px 16px', fontSize: '13px', color: '#fff', borderBottom: '1px solid #2a2d35' },
  tr: { transition: 'background 0.15s' },
  badge: { display: 'inline-block', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' },
  badgePending: { background: 'rgba(252,177,15,0.15)', color: '#fcb10f' },
  badgeCompleted: { background: 'rgba(0,231,1,0.15)', color: '#00e701' },
  badgeCancelled: { background: 'rgba(255,68,68,0.15)', color: '#ff4444' },
  badgeBanned: { background: 'rgba(255,68,68,0.15)', color: '#ff4444' },
  badgeActive: { background: 'rgba(0,231,1,0.15)', color: '#00e701' },
  btn: { background: '#00e701', color: '#000', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', marginRight: '6px' },
  btnDanger: { background: '#ff4444', color: '#fff' },
  btnOutline: { background: 'transparent', color: '#6b7280', border: '1px solid #2a2d35', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  empty: { padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '14px' },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('queued');
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ queued: 0, completed: 0, cancelled: 0, total: 0 });

  useEffect(() => {
    if (!localStorage.getItem('admin_session')) {
      navigate('/betonmines/login');
      return;
    }
    loadWithdrawals();
    loadStats();
  }, [tab]);

  async function loadWithdrawals() {
    setLoading(true);
    let query = supabase
      .from('withdrawals')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (tab !== 'all') {
      query = query.eq('status', tab);
    }

    const { data } = await query;
    setWithdrawals(data || []);
    setLoading(false);
  }

  async function loadStats() {
    const { data: all } = await supabase.from('withdrawals').select('status');
    const counts = { queued: 0, completed: 0, cancelled: 0, total: 0 };
    (all || []).forEach(w => {
      counts[w.status] = (counts[w.status] || 0) + 1;
      counts.total++;
    });
    setStats(counts);
  }

  async function updateStatus(id, newStatus, currentAmount, currentStatus) {
    const { data: withdrawal } = await supabase
      .from('withdrawals')
      .select('user_id')
      .eq('id', id)
      .single();

    if (newStatus === 'cancelled' && currentStatus === 'queued') {
      const { data: user } = await supabase
        .from('users')
        .select('balance')
        .eq('id', withdrawal.user_id)
        .single();
      
      await supabase
        .from('users')
        .update({ balance: Number(user.balance || 0) + Number(currentAmount) })
        .eq('id', withdrawal.user_id);
    }

    await supabase
      .from('withdrawals')
      .update({ status: newStatus })
      .eq('id', id);

    await loadWithdrawals();
    await loadStats();
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.title}>🛡️ Admin Dashboard</div>
        <div style={S.nav}>
          <Link to="/betonmines" style={{ ...S.navBtn, ...S.navBtnActive }}>Withdrawals</Link>
          <Link to="/betonmines/users" style={S.navBtn}>Users</Link>
          <Link to="/betonmines/manage" style={S.navBtn}>Manage</Link>
        </div>
      </div>

      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={{ ...S.statVal, ...S.statYellow }}>{stats.queued}</div>
          <div style={S.statLabel}>Queued</div>
        </div>
        <div style={S.statCard}>
          <div style={{ ...S.statVal, ...S.statGreen }}>{stats.completed}</div>
          <div style={S.statLabel}>Completed</div>
        </div>
        <div style={S.statCard}>
          <div style={{ ...S.statVal, ...S.statRed }}>{stats.cancelled}</div>
          <div style={S.statLabel}>Cancelled</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statVal}>{stats.total}</div>
          <div style={S.statLabel}>Total</div>
        </div>
      </div>

      <div style={S.tabs}>
        {['queued', 'completed', 'cancelled', 'all'].map(t => (
          <button
            key={t}
            style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div style={S.table}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={S.th}>User</th>
              <th style={S.th}>Amount</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Date</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" style={{ ...S.td, textAlign: 'center' }}>Loading...</td></tr>
            )}
            {!loading && withdrawals.length === 0 && (
              <tr><td colSpan="5" style={S.empty}>No withdrawals found</td></tr>
            )}
            {withdrawals.map(w => (
              <tr key={w.id} style={S.tr}>
                <td style={S.td}><strong>{w.username}</strong></td>
                <td style={S.td}>₿{Number(w.amount).toFixed(2)}</td>
                <td style={S.td}>
                  <span style={{ ...S.badge, ...(w.status === 'completed' ? S.badgeCompleted : w.status === 'cancelled' ? S.badgeCancelled : S.badgePending) }}>
                    {w.status}
                  </span>
                </td>
                <td style={S.td}>{new Date(w.created_at).toLocaleString()}</td>
                <td style={S.td}>
                  {w.status === 'queued' && (
                    <>
                      <button style={S.btn} onClick={() => updateStatus(w.id, 'completed', w.amount, w.status)}>
                        Complete
                      </button>
                      <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => updateStatus(w.id, 'cancelled', w.amount, w.status)}>
                        Cancel
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
